import { useEffect, useRef, useState } from 'react';
import { IngridResponse, InitializeIngridRequest, UpdateIngridRequest } from '../../../types/ingrid';
import callMiddlewareApi from '../../../utils/callMiddlewareApi';
import { useCheckoutState } from '../../../utils/checkoutState';
import { config } from '../../../utils/config';
import { replaceScriptNode, setupGeneralJSListeners } from '../../../utils/ingrid';
import useDeepCompare from '../../../utils/useDeepCompare';
import LoadingIndicator from '../../atoms/LoadingIndicator';
import './style.scss';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    _sw?: any;
  }
}
const INGRID_LOADER_HEIGHT = 461;

const Ingrid = () => {
  const { checkoutState, forceOrderUpdate } = useCheckoutState();
  const { id, basket, addresses } = checkoutState.orderState;
  const { country, currency, appId } = config;
  const widgetWrapperRef = useRef<HTMLDivElement>(null);
  const [ingridResponse, setIngridResponse] = useState<IngridResponse>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const ingridInit = useDeepCompare(ingridResponse);
  const memoizedBasket = useDeepCompare(basket);
  const postalCode = addresses?.shipping?.zipCode;

  const WEB_COMPONENT = document.querySelector('scayle-checkout') ?? document.querySelector('ay-checkout');

  const SHIPWALLET_CONTAINER = import.meta.env.DEV ? document : WEB_COMPONENT?.shadowRoot;

  const initializeIngrid = async () => {
    const _ingridresponse: IngridResponse = await callMiddlewareApi({
      endpoint: '/api/session/ingrid',
      payload: {
        appId: Number(appId),
        currency,
        orderId: id,
        search_address: { postal_code: postalCode ?? '', country },
        basket
      } as InitializeIngridRequest
    });
    setIngridResponse(_ingridresponse);
    forceOrderUpdate();
  };

  const updateSession = async () => {
    return callMiddlewareApi({
      method: 'PUT',
      endpoint: '/api/session/ingrid',
      payload: {
        sessionId: ingridResponse?.sessionId,
        appId: Number(appId),
        currency,
        orderId: id,
        search_address: { postal_code: postalCode ?? '', country },
        basket
      } as UpdateIngridRequest
    });
  };

  const performIngridUpdates = async () => {
    try {
      const sessionUpdate = await updateSession();
      if (sessionUpdate) {
        await forceOrderUpdate();
      }
    } catch (error) {
      console.error('Error updating Ingrid:', error);
    }
  };

  useEffect(() => {
    initializeIngrid();
  }, []);

  useEffect(() => {
    if (ingridInit?.html_snippet) {
      try {
        if (widgetWrapperRef.current) {
          widgetWrapperRef.current.innerHTML = ingridInit.html_snippet;
          replaceScriptNode(SHIPWALLET_CONTAINER!.getElementById('shipwallet-container'));
          setupGeneralJSListeners(setIsDataChanged, setIsLoaded);
        }
      } catch (error) {
        console.error('Error rendering Ingrid widget:', error);
      }
    }
  }, [ingridInit?.sessionId]);

  useEffect(() => {
    if (ingridInit) {
      try {
        updateSession();
      } catch (error) {
        console.error(error);
      }
    }
  }, [memoizedBasket]);

  useEffect(() => {
    if (isDataChanged) {
      performIngridUpdates().finally(() => setIsDataChanged(false));
    }
  }, [isDataChanged]);

  return (
    <div
      className="ingrid-wrapper"
      style={{
        maxHeight: isLoaded ? 'max-content' : INGRID_LOADER_HEIGHT,
        pointerEvents: isDataChanged ? 'none' : 'auto'
      }}
    >
      {ingridInit ?
        <div ref={widgetWrapperRef} />
      : <LoadingIndicator contentClassName="ingrid-loading" />}
    </div>
  );
};

export default Ingrid;
